import {
  CollectionType,
  MigrationStatusResponse,
  CompanyShopsAndEmployeesMigrationResponse,
  GetMigrationsResponse,
  MigrationResponse,
  EDomain,
  CompanySnapshotListResponse,
} from '../types/migration'
//@ts-ignore
import {
  apiGetV2,
  apiPostV2,
  apiPutV2,
  reportApiGetV3,
  reportApiPostV3,
  apiReportingGet,
  //@ts-ignore
} from '../utils/api'
import {
  apiGetAutoOnboarding,
  apiPostAutoOnboarding,
  apiPutAutoOnboarding,
  apiPostAutoOnboardingV2,
  apiGetAutoOnboardingV2,
} from '../utils/apiV2'
import { ImportSyncStatusResponse } from '../types/migration'

export const enableCompanyMigration = ({
  companyId,
  enableGlSync,
}: {
  companyId: string
  enableGlSync: boolean
}) => {
  return apiPutV2(`internal/companies/${companyId}/features`, {
    enableGlSync,
  })
}

export const getMigrationStatus = ({
  companyId,
  migrationId,
}: {
  companyId: string
  migrationId: string
}): Promise<MigrationStatusResponse> => {
  return reportApiGetV3(`internal/migration-requests/companies/${companyId}/request/${migrationId}`)
}

export const migrateCompanyShopsAndEmployees = ({
  companyId,
}: {
  companyId: string
}): Promise<unknown> => {
  return apiPostV2(`internal/companies/${companyId}/greenline/sync`)
}

export const getCompanyShopsAndEmployeesMigrationStatus = ({
  companyId,
}: {
  companyId: string
}): Promise<CompanyShopsAndEmployeesMigrationResponse> => {
  return apiGetV2(`internal/companies/${companyId}/greenline/sync`)
}

export const migrateCompanyCategoriesCollection = ({
  companyId,
  shopIds,
}: {
  companyId: string
  shopIds: string[]
}): Promise<MigrationResponse> => {
  return reportApiPostV3(
    `internal/migration-requests/companies/${companyId}?type=${CollectionType.CategoryMigration}`,
    {
      shopIds,
    },
  )
}

export const getMigrationsRequests = ({
  companyId,
}: {
  companyId: string
}): Promise<GetMigrationsResponse> => {
  return reportApiGetV3(`internal/migration-requests/companies/${companyId}`)
}

export const migrateCompanyVendorsCollection = ({
  companyId,
  shopIds,
}: {
  companyId: string
  shopIds: string[]
}): Promise<MigrationResponse> => {
  return reportApiPostV3(
    `internal/migration-requests/companies/${companyId}?type=${CollectionType.VendorMigration}`,
    {
      shopIds,
    },
  )
}

export const migrateCompanyBrandsCollection = ({
  companyId,
  shopIds,
}: {
  companyId: string
  shopIds: string[]
}): Promise<MigrationResponse> => {
  return reportApiPostV3(
    `internal/migration-requests/companies/${companyId}?type=${CollectionType.BrandMigration}`,
    {
      shopIds,
    },
  )
}

export const migrateProductsCollection = ({
  companyId,
  shopIds,
}: {
  companyId: string
  shopIds: string[]
}): Promise<MigrationResponse> => {
  return reportApiPostV3(
    `internal/migration-requests/companies/${companyId}?type=${CollectionType.ProductMigration}`,
    {
      shopIds,
    },
  )
}
export const migratePricingTempaltesCollection = ({
  companyId,
  shopIds,
}: {
  companyId: string
  shopIds: string[]
}): Promise<MigrationResponse> => {
  return reportApiPostV3(
    `internal/migration-requests/companies/${companyId}?type=${CollectionType.PricingTemplateMigration}`,
    {
      shopIds,
    },
  )
}

export function autoOnboardingSync(
  companyId: number,
  body: {
    domains: EDomain[]
    force?: boolean
  },
): any {
  return apiPostAutoOnboarding(`companies/${companyId}/sync`, { ...body })
}

export function getAutoOnboardingSyncStatus(companyId: number): Promise<ImportSyncStatusResponse> {
  return apiGetAutoOnboarding(`companies/${companyId}/sync/status`)
}

export function updateEventsForCompanyMigration(
  companyId: number,
  body: { action: string },
): Promise<{ message: string }> {
  return apiPutAutoOnboarding(`companies/${companyId}/events`, { ...body })
}

// Company Products Snapshot API functions
export const generateCompanyProductsReport = ({
  companyId,
  shopId,
  startDate,
  endDate,
}: {
  companyId: string
  shopId: string
  startDate: string
  endDate: string
}): Promise<any> => {
  const currentTimeStamp = Date.now()
  const params = new URLSearchParams({
    section: 'REPORT',
    startDate,
    endDate,
    format: 'JSON',
    generate: 'true',
    filter: 'timezoneOffset:300',
    currentTimeStamp: currentTimeStamp.toString(),
  })

  return apiReportingGet(
    `mgmt/reporting/companies/${companyId}/shops/${shopId}/reportType/COMPANY_PRODUCTS_EXPORT?${params.toString()}`,
  )
}

export const getCompanySnapshotList = ({
  companyId,
  limit = 10,
  start = 0,
}: {
  companyId: string
  limit?: number
  start?: number
}): Promise<CompanySnapshotListResponse> => {
  const params = new URLSearchParams({
    reportType: 'COMPANY_PRODUCTS_EXPORT',
    limit: limit.toString(),
    start: start.toString(),
  })

  return apiReportingGet(
    `mgmt/reporting/companies/${companyId}/report-request/list?${params.toString()}`,
  )
}

export const downloadCompanySnapshot = ({
  companyId,
  requestId,
}: {
  companyId: string
  requestId: string
}): Promise<any> => {
  return apiReportingGet(`mgmt/reporting/companies/${companyId}/generated/${requestId}`)
}

// New Product Migration API - Step-based approach
// Product Migration Step Functions - Updated according to documentation
export const productMigrationDedupeValidate = ({
  glCompanyId,
  companyId,
  shopIds,
}: {
  glCompanyId: number
  companyId: string
  shopIds: string[]
}): Promise<any> => {
  return apiPostAutoOnboardingV2(`dedupe-validate`, {
    caCompanyId: glCompanyId.toString(),
    usCompanyId: companyId,
    shopIds,
  })
}

export const productMigrationBulkCreate = ({
  glCompanyId,
}: {
  glCompanyId: number
}): Promise<any> => {
  return apiPostAutoOnboardingV2(`bulk-create`, {
    caCompanyId: glCompanyId.toString(),
  })
}

export const productMigrationUpdateShopPlatformIds = ({
  glCompanyId,
  companyId,
}: {
  glCompanyId: number
  companyId: string
}): Promise<any> => {
  return apiPostAutoOnboardingV2(`update-shop-platform-productids`, {
    caCompanyId: glCompanyId.toString(),
    usCompanyId: companyId,
  })
}

// Single endpoint to get status of all product migration steps
export const getProductMigrationStatus = ({ companyId }: { companyId: string }): Promise<any> => {
  return apiGetAutoOnboardingV2(`companies/${companyId}/status`)
}

// Product Events Management
export const getDisabledEventsCompanies = (): Promise<{ disabledCompanyIds: number[] }> => {
  return apiGetAutoOnboarding(`companies/disabled-events`)
}

export const enableProductEvents = ({ companyId }: { companyId: number }): Promise<any> => {
  return apiPutAutoOnboarding(`companies/${companyId}/events`, { action: 'enable' })
}

export const disableProductEvents = ({ companyId }: { companyId: number }): Promise<any> => {
  return apiPutAutoOnboarding(`companies/${companyId}/events`, { action: 'disable' })
}



// WEBPACK FOOTER //
// ./src/api/companyMigration.ts